// src/student/components/StudentSidebar.jsx
import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Calendar,
  CheckSquare,
  PenTool,
  Award,
  User,
  LogOut,
  X,
  Sparkles,
} from "lucide-react";
import { useStudentAuth } from "../context/StudentAuthContext";
import logoImg from "../../assets/navbar-logo.png";

const navItems = [
  { name: "Dashboard", path: "/student/dashboard", icon: LayoutDashboard },
  { name: "Daily Current Affairs", path: "/student/daily-ca", icon: FileText },
  { name: "Monthly Current Affairs", path: "/student/monthly-ca", icon: Calendar },
  { name: "Prelims Tests", path: "/student/prelims", icon: CheckSquare },
  { name: "Mains Initiatives", path: "/student/mains", icon: PenTool },
  { name: "My Results", path: "/student/results", icon: Award },
  { name: "My Profile", path: "/student/profile", icon: User },
];

const StudentSidebar = ({ isMobileOpen, setIsMobileOpen }) => {
  const { studentLogout, studentUser } = useStudentAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    studentLogout();
    navigate("/login");
  };

  return (
    <>
      {/* Mobile Backdrop */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 z-40 md:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 bottom-0 left-0 z-50 w-[240px] flex flex-col bg-[#0b1528] text-slate-300 border-r border-slate-800 transition-transform duration-200 ease-in-out
          ${isMobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
        `}
      >
        {/* Header Branding */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800 bg-[#070e1c]">
          <div className="flex items-center gap-2.5 overflow-hidden">
            <div className="w-8 h-8 rounded-lg bg-white p-1 flex-shrink-0 flex items-center justify-center shadow-xs">
              <img
                src={logoImg}
                alt="Selva's IAS Academy"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="min-w-0">
              <span className="block font-bold text-xs text-white tracking-wide truncate">
                SELVA'S <span className="text-amber-400">IAS</span>
              </span>
              <span className="block text-[10px] text-amber-300 font-semibold tracking-wider uppercase truncate flex items-center gap-1">
                <Sparkles size={10} /> Free Initiative Portal
              </span>
            </div>
          </div>

          <button
            onClick={() => setIsMobileOpen(false)}
            className="md:hidden text-slate-400 hover:text-white p-1 rounded"
          >
            <X size={18} />
          </button>
        </div>

        {/* Student Badge Card */}
        <div className="px-4 py-3 bg-slate-900/60 border-b border-slate-800/80">
          <p className="text-[11px] text-slate-400 font-medium">Logged in Aspirant</p>
          <p className="text-xs font-bold text-white truncate mt-0.5">
            {studentUser?.name || "UPSC Aspirant"}
          </p>
          <span className="inline-block mt-1 text-[10px] font-mono text-emerald-400 bg-emerald-950/60 border border-emerald-800/50 px-1.5 py-0.5 rounded">
            {studentUser?.registrationId || "SIA-ASPIRANT"}
          </span>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setIsMobileOpen(false)}
                className={({ isActive }) => `
                  flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-xs font-medium transition-colors
                  ${
                    isActive
                      ? "bg-red-900 text-white font-semibold shadow-xs"
                      : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/60"
                  }
                `}
              >
                <Icon size={16} className="flex-shrink-0 opacity-90" />
                <span className="truncate">{item.name}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* Logout Bottom Bar */}
        <div className="p-3 border-t border-slate-800 bg-[#070e1c]/60">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold text-rose-400 hover:text-rose-300 hover:bg-rose-950/40 transition-colors"
          >
            <LogOut size={15} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default StudentSidebar;
